import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, RefreshCw, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export const PrescriptionEditModal = ({
  open,
  onOpenChange,
  prescription,
  patient,
  onSuccess,
  onConflict
}) => {
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_phone: '',
    patient_age: '',
    patient_gender: 'male',
    patient_email: '',
    patient_address: '',
    visit_date: '',
    follow_up_date: '',
    notes: '',
    diet: '',
    lifestyle: ''
  });

  const [saving, setSaving] = useState(false);
  const [conflictError, setConflictError] = useState(null);

  useEffect(() => {
    if (open && prescription) {
      setConflictError(null);
      setFormData({
        patient_name: prescription.patient_name || patient?.name || '',
        patient_phone: patient?.phone || prescription.phone || '',
        patient_age: patient?.age !== undefined ? String(patient.age) : '',
        patient_gender: patient?.gender || 'male',
        patient_email: patient?.email || '',
        patient_address: patient?.address || '',
        visit_date: prescription.visit_date || prescription.date || '',
        follow_up_date: prescription.follow_up_date || '',
        notes: prescription.notes || '',
        diet: prescription.diet || '',
        lifestyle: prescription.lifestyle || ''
      });
    }
  }, [open, prescription, patient]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_name.trim()) {
      toast.error('Patient name is required');
      return;
    }

    setSaving(true);
    setConflictError(null);

    const payload = {
      expected_version: prescription?.version || 1,
      patient_name: formData.patient_name.trim(),
      notes: formData.notes,
      diet: formData.diet,
      lifestyle: formData.lifestyle,
      follow_up_date: formData.follow_up_date || '',
      visit_date: formData.visit_date || ''
    };

    if (formData.patient_phone) payload.patient_phone = formData.patient_phone.trim();
    if (formData.patient_email) payload.patient_email = formData.patient_email.trim();
    if (formData.patient_address) payload.patient_address = formData.patient_address.trim();
    if (formData.patient_gender) payload.patient_gender = formData.patient_gender;
    if (formData.patient_age !== '') {
      const ageNum = parseInt(formData.patient_age, 10);
      if (!isNaN(ageNum)) {
        payload.patient_age = ageNum;
      }
    }

    try {
      const res = await api.patch(`/prescriptions/${prescription.id}`, payload);
      const data = res.data?.data || res.data;
      toast.success('Prescription updated successfully');
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.message || err.response?.data?.detail || 'Failed to update prescription';

      if (status === 409) {
        setConflictError(detail);
      } else if (status === 400 && detail.includes('immutable')) {
        toast.error('This prescription has been completed or cancelled and can no longer be edited.');
        onOpenChange(false);
        onConflict?.();
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReload = () => {
    onConflict?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Edit Prescription #{prescription?.id}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Update clinical recommendations and patient details before fulfillment completion.
          </DialogDescription>
        </DialogHeader>

        {conflictError && (
          <div className="p-3.5 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3 text-amber-900 text-sm">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-950">Concurrency Conflict</p>
              <p className="mt-0.5 text-xs text-amber-800">{conflictError}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleReload}
                className="mt-2 text-xs h-7 border-amber-400 bg-white hover:bg-amber-100 text-amber-900"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload Latest Version
              </Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label htmlFor="patient_name" className="text-xs font-semibold">
                Patient Name <span className="text-rose-500">*</span>
              </Label>
              <Input
                id="patient_name"
                name="patient_name"
                value={formData.patient_name}
                onChange={handleChange}
                placeholder="Patient Full Name"
                className="mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="patient_phone" className="text-xs font-semibold">
                Phone Number
              </Label>
              <Input
                id="patient_phone"
                name="patient_phone"
                value={formData.patient_phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            <div>
              <Label htmlFor="patient_age" className="text-xs font-semibold">
                Age
              </Label>
              <Input
                id="patient_age"
                name="patient_age"
                type="number"
                min="0"
                max="150"
                value={formData.patient_age}
                onChange={handleChange}
                placeholder="Age in years"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="patient_gender" className="text-xs font-semibold">
                Gender
              </Label>
              <select
                id="patient_gender"
                name="patient_gender"
                value={formData.patient_gender}
                onChange={handleChange}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label htmlFor="patient_email" className="text-xs font-semibold">
                Email
              </Label>
              <Input
                id="patient_email"
                name="patient_email"
                type="email"
                value={formData.patient_email}
                onChange={handleChange}
                placeholder="patient@example.com"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="patient_address" className="text-xs font-semibold">
              Address
            </Label>
            <Input
              id="patient_address"
              name="patient_address"
              value={formData.patient_address}
              onChange={handleChange}
              placeholder="City, State, Postal Address"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label htmlFor="visit_date" className="text-xs font-semibold">
                Visit Date
              </Label>
              <Input
                id="visit_date"
                name="visit_date"
                type="date"
                value={formData.visit_date}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="follow_up_date" className="text-xs font-semibold">
                Follow-up Date
              </Label>
              <Input
                id="follow_up_date"
                name="follow_up_date"
                type="date"
                value={formData.follow_up_date}
                onChange={handleChange}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes" className="text-xs font-semibold">
              Doctor Clinical Notes & Observations
            </Label>
            <Textarea
              id="notes"
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              placeholder="Clinical symptoms, diagnosis notes, dosage guidance..."
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <Label htmlFor="diet" className="text-xs font-semibold">
                Dietary Recommendations (Pathya/Apathya)
              </Label>
              <Textarea
                id="diet"
                name="diet"
                rows={2}
                value={formData.diet}
                onChange={handleChange}
                placeholder="Recommended foods, dietary restrictions..."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="lifestyle" className="text-xs font-semibold">
                Lifestyle & Dinacharya Advice
              </Label>
              <Textarea
                id="lifestyle"
                name="lifestyle"
                rows={2}
                value={formData.lifestyle}
                onChange={handleChange}
                placeholder="Pranayama, exercise, sleep hygiene advice..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionEditModal;
